import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { DfcThemeMode } from '../Utils/dfcSurface';

/**
 * Light / dark mode for the DFC Fitness Village theme (`company.theme === 'dfcVillageTheme'`).
 *
 * The app has no other notion of dark mode — MUI's `palette.mode` is never set and nothing
 * reads it — so this context is the single source of truth. Components never read it
 * directly: they call `useDfcSurface()` (src/hooks/useDfcSurface.ts), which resolves the
 * mode into the `DfcSurface` token scale every styled component takes as a prop.
 *
 * The header's theme switch sets it. A visitor's choice is remembered on this device; with no
 * choice yet every visit starts light — DFC's default look — and the OS setting is not read.
 */
export const DFC_THEME_MODE_STORAGE_KEY = 'dfcThemeMode';

/**
 * Mirrors the mode onto `<html>` so a brand stylesheet can vary its colours by mode
 * (`asaBrand.ts`). Nothing in DFC's styles reads it.
 */
export const VILLAGE_MODE_ATTRIBUTE = 'data-village-mode';

export interface DfcThemeModeContextProps {
  mode: DfcThemeMode;
  setMode: (mode: DfcThemeMode) => void;
}

const isDfcThemeMode = (value: unknown): value is DfcThemeMode =>
  value === 'light' || value === 'dark';

const readStoredDfcThemeMode = (): DfcThemeMode => {
  try {
    const stored = window.localStorage.getItem(DFC_THEME_MODE_STORAGE_KEY);
    if (isDfcThemeMode(stored)) return stored;
  } catch {
    // Storage can be unavailable (private mode, blocked site data) — fall through.
  }
  return 'light';
};

const persistDfcThemeMode = (mode: DfcThemeMode) => {
  try {
    window.localStorage.setItem(DFC_THEME_MODE_STORAGE_KEY, mode);
  } catch {
    // The choice simply won't survive a reload.
  }
};

export const DfcThemeModeContext = createContext<DfcThemeModeContextProps>({
  mode: 'light',
  setMode: () => undefined,
});

export const DfcThemeModeProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [mode, setModeState] = useState<DfcThemeMode>(readStoredDfcThemeMode);

  const setMode = useCallback((next: DfcThemeMode) => {
    setModeState(next);
    persistDfcThemeMode(next);
  }, []);

  useLayoutEffect(() => {
    document.documentElement.setAttribute(VILLAGE_MODE_ATTRIBUTE, mode);
  }, [mode]);

  const value = useMemo(() => ({ mode, setMode }), [mode, setMode]);

  return <DfcThemeModeContext.Provider value={value}>{children}</DfcThemeModeContext.Provider>;
};

export const useDfcThemeMode = (): DfcThemeModeContextProps => useContext(DfcThemeModeContext);
